import { ChangeEvent, useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import MDBox from "mdComponents/MDBox";
import MDButton from "mdComponents/MDButton";
import MDTypography from "mdComponents/MDTypography";
import { LocationCoupon, LocationImage } from "../models/hostModel";
import locationsService from "services/locationsService";

const MAX_MEDIA_ITEMS = 5;

const mediaCardSx = {
  width: 224,
  minWidth: 224,
  borderRadius: "16px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  p: 1,
};

const emptySx = {
  minHeight: 132,
  borderRadius: "16px",
  border: "1px dashed #cbd5e1",
  backgroundColor: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  p: 2,
};

const scrollListSx = {
  display: "flex",
  gap: 1.25,
  overflowX: "auto",
  pb: 0.5,
};

type LocationPhotoManagerProps = {
  hostId: number;
  onImagesChanged?: (images: LocationImage[]) => void;
};

export const LocationPhotoManager = ({ hostId, onImagesChanged }: LocationPhotoManagerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const onImagesChangedRef = useRef(onImagesChanged);
  const [images, setImages] = useState<LocationImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingImage, setSavingImage] = useState<{ id: number; action: "primary" | "delete" }>();

  useEffect(() => {
    onImagesChangedRef.current = onImagesChanged;
  }, [onImagesChanged]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const result = await locationsService.fetchLocationImages(hostId);
      if (!mounted) return;
      setImages(result);
      onImagesChangedRef.current?.(result);
      setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [hostId]);

  const chooseImages = () => inputRef.current?.click();

  const onFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []).slice(0, MAX_MEDIA_ITEMS - images.length);
    event.target.value = "";
    if (!selected.length || uploading || images.length >= MAX_MEDIA_ITEMS) return;

    setUploading(true);
    const nextImages = [...images];
    let failed = false;
    for (const file of selected) {
      if (nextImages.length >= MAX_MEDIA_ITEMS) break;
      const uploaded = await locationsService.uploadLocationImage(hostId, file);
      if (uploaded) nextImages.push(uploaded);
      else failed = true;
    }
    setImages(nextImages);
    onImagesChanged?.(nextImages);
    setUploading(false);
    if (failed) window.alert("Some photos were not uploaded. Please try those photos again.");
  };

  const makePrimary = async (imageId: number) => {
    if (savingImage || uploading) return;
    setSavingImage({ id: imageId, action: "primary" });
    const saved = await locationsService.setPrimaryLocationImage(hostId, imageId);
    if (saved) {
      const nextImages = images.map((image) => ({ ...image, isPrimary: image.id === imageId }));
      setImages(nextImages);
      onImagesChanged?.(nextImages);
    } else {
      window.alert("Unable to set the primary photo. Please try again.");
    }
    setSavingImage(undefined);
  };

  const deleteImage = async (imageId: number) => {
    if (savingImage || uploading) return;
    if (!window.confirm("Delete this location photo?")) return;
    setSavingImage({ id: imageId, action: "delete" });
    const nextImages = await locationsService.deleteLocationImage(hostId, imageId);
    if (nextImages) {
      setImages(nextImages);
      onImagesChanged?.(nextImages);
    } else {
      window.alert("Unable to delete the photo. Please try again.");
    }
    setSavingImage(undefined);
  };

  if (loading) return <MediaLoading label="Loading location photos..." />;

  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        hidden
        onChange={onFilesSelected}
      />

      {images.length > 0 ? (
        <MDBox sx={scrollListSx}>
          {images.map((image) => {
            const saving = savingImage?.id === image.id;
            return (
              <MDBox
                key={image.id}
                sx={{
                  ...mediaCardSx,
                  borderColor: image.isPrimary ? "#0878ed" : "#e2e8f0",
                }}
              >
                <MDBox
                  component="img"
                  src={image.imageUrl}
                  alt="Location"
                  sx={{ width: "100%", height: 142, objectFit: "cover", borderRadius: "11px" }}
                />
                {image.isPrimary && (
                  <MDBox
                    mt={-5}
                    ml={1}
                    mb={2}
                    display="inline-flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                      minHeight: 26,
                      borderRadius: "13px",
                      px: 1,
                      backgroundColor: "#0878ed",
                      color: "#ffffff",
                      position: "relative",
                    }}
                  >
                    <StarIcon sx={{ fontSize: 14 }} />
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#ffffff" }}>
                      Primary
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox display="flex" alignItems="center" gap={0.75} mt={image.isPrimary ? 0 : 1}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    disabled={image.isPrimary || saving || uploading}
                    onClick={() => makePrimary(image.id)}
                    sx={{ minHeight: 38, borderRadius: "12px", textTransform: "none", fontWeight: 900 }}
                    startIcon={
                      saving && savingImage?.action === "primary" ? (
                        <CircularProgress size={15} />
                      ) : (
                        <StarBorderIcon />
                      )
                    }
                  >
                    {image.isPrimary ? "Primary" : "Make Primary"}
                  </MDButton>
                  <IconButton
                    aria-label="Delete location photo"
                    color="error"
                    disabled={saving || uploading}
                    onClick={() => deleteImage(image.id)}
                  >
                    {saving && savingImage?.action === "delete" ? (
                      <CircularProgress size={18} color="error" />
                    ) : (
                      <DeleteOutlineIcon />
                    )}
                  </IconButton>
                </MDBox>
              </MDBox>
            );
          })}
        </MDBox>
      ) : (
        <MDBox sx={emptySx}>
          <ImageOutlinedIcon sx={{ color: "#64748b", fontSize: 30 }} />
          <MDTypography variant="button" color="dark" fontWeight="bold" mt={1}>
            No location photos yet
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="medium" mt={0.5}>
            Google imagery will continue to appear until you upload a photo.
          </MDTypography>
        </MDBox>
      )}

      <MDButton
        variant="gradient"
        color="info"
        disabled={uploading || images.length >= MAX_MEDIA_ITEMS}
        onClick={chooseImages}
        sx={{ minHeight: 42, borderRadius: "14px", textTransform: "none", fontWeight: 900 }}
        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <AddPhotoAlternateIcon />}
      >
        {uploading
          ? "Uploading Photos..."
          : images.length >= MAX_MEDIA_ITEMS
          ? "5 Photo Limit Reached"
          : `Add Photos (${images.length}/5)`}
      </MDButton>
    </MDBox>
  );
};

type LocationCouponManagerProps = {
  hostId: number;
};

export const LocationCouponManager = ({ hostId }: LocationCouponManagerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [coupons, setCoupons] = useState<LocationCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number>();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const result = await locationsService.fetchLocationCoupons(hostId);
      if (!mounted) return;
      setCoupons(result);
      setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [hostId]);

  const chooseCoupons = () => inputRef.current?.click();

  const onFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []).slice(0, MAX_MEDIA_ITEMS - coupons.length);
    event.target.value = "";
    if (!selected.length || uploading || coupons.length >= MAX_MEDIA_ITEMS) return;

    setUploading(true);
    const nextCoupons = [...coupons];
    let failed = false;
    for (const file of selected) {
      if (nextCoupons.length >= MAX_MEDIA_ITEMS) break;
      const uploaded = await locationsService.uploadLocationCoupon(hostId, file);
      if (uploaded) nextCoupons.push(uploaded);
      else failed = true;
    }
    setCoupons(nextCoupons);
    setUploading(false);
    if (failed) window.alert("Some coupons were not uploaded. Please try those images again.");
  };

  const deleteCoupon = async (couponId: number) => {
    if (deletingId !== undefined || uploading) return;
    if (!window.confirm("Delete this coupon? It will no longer appear on the public location profile.")) return;
    setDeletingId(couponId);
    const result = await locationsService.deleteLocationCoupon(hostId, couponId);
    if (result) setCoupons(result);
    else window.alert("Unable to delete the coupon. Please try again.");
    setDeletingId(undefined);
  };

  if (loading) return <MediaLoading label="Loading coupons..." />;

  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        hidden
        onChange={onFilesSelected}
      />

      {coupons.length > 0 ? (
        <MDBox sx={scrollListSx}>
          {coupons.map((coupon) => (
            <MDBox key={coupon.id} sx={mediaCardSx}>
              <MDBox
                sx={{
                  width: "100%",
                  height: 142,
                  borderRadius: "11px",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                  border: "1px solid #f1f5f9",
                }}
              >
                <MDBox
                  component="img"
                  src={coupon.imageUrl}
                  alt="Coupon"
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </MDBox>
              <MDBox display="flex" alignItems="center" mt={0.75}>
                <MDBox display="flex" alignItems="center" gap={0.75} flex={1}>
                  <ConfirmationNumberOutlinedIcon sx={{ color: "#0878ed", fontSize: 19 }} />
                  <MDTypography variant="caption" color="dark" fontWeight="bold">
                    Coupon
                  </MDTypography>
                </MDBox>
                <IconButton
                  aria-label="Delete coupon"
                  color="error"
                  disabled={deletingId !== undefined || uploading}
                  onClick={() => deleteCoupon(coupon.id)}
                >
                  {deletingId === coupon.id ? (
                    <CircularProgress size={18} color="error" />
                  ) : (
                    <DeleteOutlineIcon />
                  )}
                </IconButton>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      ) : (
        <MDBox sx={emptySx}>
          <ConfirmationNumberOutlinedIcon sx={{ color: "#64748b", fontSize: 30 }} />
          <MDTypography variant="button" color="dark" fontWeight="bold" mt={1}>
            No coupons uploaded
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="medium" mt={0.5}>
            Coupon offers will appear on the public profile after upload.
          </MDTypography>
        </MDBox>
      )}

      <MDButton
        variant="gradient"
        color="info"
        disabled={uploading || coupons.length >= MAX_MEDIA_ITEMS}
        onClick={chooseCoupons}
        sx={{ minHeight: 42, borderRadius: "14px", textTransform: "none", fontWeight: 900 }}
        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <ConfirmationNumberOutlinedIcon />}
      >
        {uploading
          ? "Uploading Coupons..."
          : coupons.length >= MAX_MEDIA_ITEMS
          ? "5 Coupon Limit Reached"
          : `Add Coupons (${coupons.length}/5)`}
      </MDButton>
    </MDBox>
  );
};

const MediaLoading = ({ label }: { label: string }) => (
  <MDBox minHeight={84} display="flex" alignItems="center" justifyContent="center" gap={1}>
    <CircularProgress size={20} />
    <MDTypography variant="caption" color="text" fontWeight="bold">
      {label}
    </MDTypography>
  </MDBox>
);
